import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged,
  updateProfile,
  GoogleAuthProvider,
  signInWithPopup,
  deleteUser,
  signInAnonymously
} from 'firebase/auth';
import { doc, setDoc, getDoc, deleteDoc, collection, query, where, getDocs, writeBatch } from 'firebase/firestore';
import { setAnalyticsCollectionEnabled } from 'firebase/analytics';
import { auth, db, analytics } from '../firebase';
import { getChildSession, setChildSession, clearChildSession, loadChildProfile } from '../utils/childAuthHelper';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  async function signup(email, password, displayName, role = 'child') {
    try {
      const result = await createUserWithEmailAndPassword(auth, email, password);
      await updateProfile(result.user, { displayName });
      
      // Create user profile in Firestore
      await setDoc(doc(db, 'users', result.user.uid), {
        uid: result.user.uid,
        email: email,
        displayName: displayName,
        role: role, // 'parent' or 'child'
        createdAt: new Date().toISOString(),
        allowance: 0,
        totalEarned: 0,
        streak: 0,
        badges: [],
        avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(displayName)}&background=4CAF50&color=fff&size=128`
      });
      
      return result;
    } catch (error) {
      throw error;
    }
  }

  async function login(email, password) {
    const result = await signInWithEmailAndPassword(auth, email, password);
    await fetchUserProfile(result.user.uid);
    return result;
  }

  async function signInWithGoogle(role = 'parent') {
    try {
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({
        prompt: 'select_account' // Always show account selection
      });
      
      const result = await signInWithPopup(auth, provider);
      const user = result.user;
      
      // Check if user profile already exists
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      
      if (!userDoc.exists()) {
        // New user - create profile
        await setDoc(doc(db, 'users', user.uid), {
          uid: user.uid,
          email: user.email,
          displayName: user.displayName || user.email.split('@')[0],
          role: role,
          createdAt: new Date().toISOString(),
          allowance: 0,
          totalEarned: 0,
          streak: 0,
          badges: [],
          avatar: user.photoURL || `https://ui-avatars.com/api/?name=${encodeURIComponent(user.displayName || 'User')}&background=4CAF50&color=fff&size=128`,
          authProvider: 'google'
        });
      }
      
      await fetchUserProfile(user.uid);
      return { user, isNewUser: !userDoc.exists() };
    } catch (error) {
      throw error;
    }
  }

  /**
   * Login a child by setting context state directly (no Firebase Auth)
   * This is the correct way to log in a child — setChildSession alone doesn't
   * update React state, so AuthContext would still show null user after navigation.
   */
  async function loginChild(childData) {
    try {
      // Sign into Firebase anonymously so child gets a real auth token.
      // This lets Firestore rules pass and enables AI learning persistence (13+ COPPA-safe).
      // Anonymous auth collects no PII — COPPA compliant.
      let anonymousUid = null;
      try {
        // Only sign in anonymously if no parent is currently signed in
        if (!auth.currentUser) {
          const anonResult = await signInAnonymously(auth);
          anonymousUid = anonResult.user.uid;
        }
      } catch (anonErr) {
        console.warn('Anonymous auth failed, child will use synthetic UID:', anonErr.message);
      }

      // Persist to localStorage
      setChildSession({ ...childData, anonymousUid });

      // Load full profile from Firestore (ensures we have dateOfBirth etc.)
      let profile = childData;
      try {
        profile = await loadChildProfile(childData.familyId, childData.childId);
      } catch (err) {
        console.warn('Could not load full child profile, using auth data:', err);
      }

      const childUser = {
        uid: `child:${childData.childId}`,
        isChild: true,
        childId: childData.childId,
        familyId: childData.familyId,
        displayName: childData.firstName || profile.firstName,
        email: null,
        photoURL: childData.avatar || profile.avatar,
        // anonymousUid: real Firebase Auth UID for Firestore writes (AI learning, etc.)
        anonymousUid: anonymousUid || childData.anonymousUid || null
      };

      setCurrentUser(childUser);
      setUserProfile({
        ...profile,
        role: 'child',
        displayName: profile.firstName || childData.firstName,
        familyId: childData.familyId
      });

      // COPPA: always disable analytics for child accounts
      setAnalyticsCollectionEnabled(analytics, false);
    } catch (error) {
      console.error('Error in loginChild:', error);
      throw error;
    }
  }

  async function logout() {
    // Clear child session if present
    const childSession = getChildSession();
    if (childSession) {
      clearChildSession();
    }
    
    setUserProfile(null);
    setCurrentUser(null);
    
    // Only sign out from Firebase Auth if there's a Firebase user
    if (auth.currentUser) {
      return signOut(auth);
    }
  }

  // Re-fetch and update profile in-memory — call after any profile Firestore write
  // so React state updates immediately without a page reload
  async function refreshProfile() {
    try {
      if (!auth.currentUser) return;
      await fetchUserProfile(auth.currentUser.uid);
    } catch (err) {
      console.warn('refreshProfile error:', err);
    }
  }

  async function fetchUserProfile(uid) {
    try {
      const userDoc = await getDoc(doc(db, 'users', uid));
      if (userDoc.exists()) {
        const profileData = { id: userDoc.id, ...userDoc.data() };
        setUserProfile(profileData);
        
        // COPPA Compliance: Enable analytics ONLY for parent accounts (18+)
        // Children's data is NOT tracked to comply with COPPA regulations
        if (profileData.role === 'parent') {
          setAnalyticsCollectionEnabled(analytics, true);
          console.log('✅ Analytics enabled for parent account (18+)');
        } else {
          setAnalyticsCollectionEnabled(analytics, false);
          console.log('🔒 Analytics disabled for child account (COPPA compliance)');
        }
        
        return profileData;
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
    }
  }

  async function deleteAccount() {
    if (!currentUser) throw new Error('Must be logged in to delete account');
    
    try {
      const uid = currentUser.uid;
      const batch = writeBatch(db);
      
      // Delete user's tasks
      const tasksQuery = query(collection(db, 'tasks'), where('createdBy', '==', uid));
      const tasksSnapshot = await getDocs(tasksQuery);
      tasksSnapshot.docs.forEach(doc => {
        batch.delete(doc.ref);
      });
      
      // Delete user's shopping list items
      const shoppingQuery = query(collection(db, 'shopping'), where('addedBy', '==', uid));
      const shoppingSnapshot = await getDocs(shoppingQuery);
      shoppingSnapshot.docs.forEach(doc => {
        batch.delete(doc.ref);
      });
      
      // Delete user's calendar events
      const calendarQuery = query(collection(db, 'calendar'), where('createdBy', '==', uid));
      const calendarSnapshot = await getDocs(calendarQuery);
      calendarSnapshot.docs.forEach(doc => {
        batch.delete(doc.ref);
      });
      
      // Commit all deletions
      await batch.commit();
      
      // Delete user profile from Firestore
      await deleteDoc(doc(db, 'users', uid));
      
      // Delete Firebase Auth account
      await deleteUser(auth.currentUser);
      
      // Clear local state
      setUserProfile(null);
      setCurrentUser(null);
      
      return true;
    } catch (error) {
      console.error('Error deleting account:', error);
      throw error;
    }
  }

  useEffect(() => {
    // Check for child session first (COPPA-compliant auth)
    const childSessionData = getChildSession();
    if (childSessionData) {
      // Child is logged in (no Firebase Auth)
      const loadChildData = async () => {
        try {
          const childProfile = await loadChildProfile(
            childSessionData.familyId, 
            childSessionData.childId
          );
          
          // Create a compatible "user" object for children
          const childUser = {
            uid: `child:${childSessionData.childId}`, // Prefixed UID for task queries
            isChild: true,
            childId: childSessionData.childId,
            familyId: childSessionData.familyId,
            displayName: childSessionData.firstName,
            email: null, // No email for children (COPPA)
            photoURL: childProfile.avatar
          };
          
          setCurrentUser(childUser);
          
          // Set user profile with child data
          setUserProfile({
            ...childProfile,
            role: 'child',
            displayName: childProfile.firstName,
            familyId: childSessionData.familyId
          });
          
          // COPPA: Analytics disabled for children
          setAnalyticsCollectionEnabled(analytics, false);
          console.log('🔒 Analytics disabled for child account (COPPA compliance)');
          
          setLoading(false);
        } catch (error) {
          console.error('Error loading child profile:', error);
          // Clear invalid child session
          clearChildSession();
          setLoading(false);
        }
      };
      
      loadChildData();
      
      // Don't set up Firebase Auth listener for child sessions
      return;
    }
    
    // Normal Firebase Auth for parents
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      if (user) {
        await fetchUserProfile(user.uid);
      } else {
        setUserProfile(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    userProfile,
    signup,
    login,
    loginChild,
    signInWithGoogle,
    logout,
    fetchUserProfile,
    refreshProfile,
    deleteAccount
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
